import { signIn, getUserById } from './botFactoryAPI';
import { cookieMaxAgeFromJwt } from './guestGate';
import { commitMemberOrg } from './memberOrg';

// Define User Type
export interface User {
  firstname: string;
  lastname: string;
  salutation?: string;
  email: string;
  username: string;
  org?: { orgId: string; orgName: string };
  emailVerified?: boolean;
  role: string[];
  mobile: string;
  pic: string[];
  footerProfile?: any | null;
}

// Handle sign-in process with retries and error handling
// Add error boundaries — wrap main usage in try/catch to prevent white screen
// Add user feedback via status callback
type StatusCallback = (status: 'loading' | 'success' | 'error', message?: string) => void;

function checkCookieAccessibility(cookieName: string): boolean {
  try {
    const cookies = document.cookie;
    return cookies.includes(`${cookieName}=`);
  } catch (error) {
    console.error(`Failed to access cookie ${cookieName}:`, error);
    return false;
  }
}

// Set auth cookies with retries and progressive backoff
async function setAuthCookies(
  token: string,
  userId: string,
  secure = true,
  maxRetries = 6,
  initialDelay = 200,
  statusCallback?: StatusCallback
) {
  if (statusCallback) statusCallback('loading', 'Starting cookie setup');
  console.log(`[AUTH] setAuthCookies starting | secure=${secure} | maxRetries=${maxRetries} | initialDelay=${initialDelay}ms`);
  const startTime = Date.now();

  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (statusCallback)
        statusCallback('loading', `Attempt ${attempt} to set cookies`);

      // ⛔ FIX 3 (2026-08-14) — a REAL Max-Age, taken from the JWT's own lifetime.
      // These were SESSION cookies (no Max-Age, no Expires). Chrome's session-restore keeps those
      // alive across restarts indefinitely, so a login dead for weeks still presented a `token`
      // cookie — and IndexPageGuest's gate only checks that the cookie EXISTS, never that the JWT
      // is valid. That is what armed the confirmed guest redirect loop. Aligning the cookie to
      // `exp` means the browser drops it exactly when the credential actually dies.
      // ⚠️ This changes no expiry DECISION anywhere — see the deferred JWT-exp item in
      // porting/NEXT.md §5. It only stops the cookie outliving its own token.
      // ⚠️ MAIN PARITY: origin/main has the same no-Max-Age bug. This is a deliberate
      // fix-ahead-of-main divergence, recorded like the (b)-ruling matcher fix.
      const maxAge = cookieMaxAgeFromJwt(token);
      const cookieSettings = [
        `token=${token}; path=/; Max-Age=${maxAge}; ${secure ? 'Secure' : ''}; SameSite=Lax`,
        `userId=${userId}; path=/; Max-Age=${maxAge}; ${secure ? 'Secure' : ''}; SameSite=Lax`,
      ];

      for (const cookie of cookieSettings) {
        document.cookie = cookie;
      }

      const tokenAccessible = checkCookieAccessibility('token');
      const userIdAccessible = checkCookieAccessibility('userId');

      if (tokenAccessible && userIdAccessible) {
        window.dispatchEvent(new Event('tokenUpdated'));
        console.log(`[AUTH] Cookies set on attempt ${attempt} | elapsed=${Date.now() - startTime}ms`);
        if (statusCallback)
          statusCallback('success', 'Cookies set successfully');
        return true;
      }

      throw new Error('Cookies not set properly');
    } catch (error) {
      lastError = error;
      const delay = initialDelay * Math.pow(2, attempt - 1);
      console.warn(`[AUTH] Cookie attempt ${attempt}/${maxRetries} failed | next retry in ${delay}ms | elapsed=${Date.now() - startTime}ms`);
      if (statusCallback)
        statusCallback('error', `Attempt ${attempt} failed: ${error}`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  console.error(`[AUTH] Failed to set cookies after ${maxRetries} retries | elapsed=${Date.now() - startTime}ms`, lastError);
  if (statusCallback)
    statusCallback('error', 'Failed to set auth cookies after retries');
  return false;
}



export const handleSignIn = async (
  email: string,
  password: string,
  setUserState: (user: User) => void,
  setErrorMessage: (message: string) => void,
  botId?: string,
  prompt?: string
) => {
  try {
    if (!email || !password) {
      alert('Sign in failed');
      return;
    }

    // Parallelized sign-in with timeout protection
    const signInPromise = signIn(email, password);
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('⏱️ SignIn timeout')), 15000)
    );

    const response = await Promise.race([signInPromise, timeout]);
    console.log('✅ Sign In Success:', response);

    // Detect environment (local or production)
    const isLocal = window.location.protocol === 'http:';

    // Set timestamp
    localStorage.setItem('tokenTimestamp', Date.now().toString());



    // 🔧 UPDATED: Set cookie and wait to fully propagate before fetching user
    //setAuthCookies(response.token, response.userId, !isLocal);
    //window.dispatchEvent(new Event('tokenUpdated'));

    const cookiesSet = await setAuthCookies(
      response.token,
      response.userId,
      !isLocal
    );
    window.dispatchEvent(new Event('tokenUpdated'));

    if (!cookiesSet) {
      const errorData = {
        error: 'AUTH_COOKIE_SETUP_FAILED',
        message: 'Failed to set authentication cookies',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };
      const encodedError = encodeURIComponent(JSON.stringify(errorData));
      window.location.href = `/pages/setup/NotFound?error=${encodedError}`;
      return;
    }


    await new Promise((resolve) => setTimeout(resolve, 200));

    // Fetch user data with retries
    let userData: User | null = null;
    let attempts = 0;

    while (attempts < 5 && !userData) {
      attempts++;
      userData = await getUserById(response.userId, response.token);
      console.log(`Attempt ${attempts}:`, userData);

      if (!userData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    if (!userData) {
      console.log('auth.tsx : Final Result: Failed to load userData after 5 attempts');
      alert('auth.tsx : Cannot load Server Cookies for Auth');
      return;
    }

    console.log('Final Result: Successfully loaded userData:', userData);

    // Proceed with userData
    setUserState({
      firstname: userData.firstname ?? '',
      lastname: userData.lastname ?? '',
      salutation: userData.salutation ?? '',
      email: userData.email ?? '',
      username: userData.username ?? '',
      org: userData.org ?? { orgId: '', orgName: '' },
      emailVerified: userData.emailVerified ?? false,
      role: Array.isArray(userData.role) ? userData.role : ['guest'],
      mobile: userData.mobile ?? '',
      pic: Array.isArray(userData.pic)
        ? userData.pic
        : ['/assets/avatar-icon.jpg'],
      footerProfile: userData.footerProfile ?? null,
    });

    console.log('📩 Updated user:', userData);

    // ⛔ SYNCHRONOUS, before the redirect (2026-10-28). This was deferred to requestIdleCallback /
    // setTimeout(100) while location.assign below fired immediately — when the navigation won,
    // /ibot loaded with no 'user', fell back to the placeholder user (org is a STRING), sent
    // org.orgId '' and the relay skipped every chatHistory POST ("missing identity").
    localStorage.setItem('user', JSON.stringify(userData));
    commitMemberOrg(localStorage, userData);

    // ✅ Correct position for redirect
    const url = new URL(window.location.origin);
    url.pathname = '/ibot';
    if (botId) url.searchParams.set('id', botId);
    if (prompt) url.searchParams.set('prompt', encodeURIComponent(prompt));
    
    window.location.assign(url.toString());
    console.log('✅ Redirecting to:', url.toString());
  } catch (error) {
    console.error('❌ SignIn Error:', error);
    setErrorMessage('Signin failed. Try again.');
  }
};
